import type { TabOptions } from 'reakit/Tab';
import { useTab as useReakitTab } from 'reakit/Tab';
import { createHook, createComponent } from 'reakit-system';
import type { SystemStyleObject } from '@styled-system/css';
import type { ButtonHTMLProps } from '../../Button/Button';
import type { StylesOptions } from '../../hooks';
import { useTheme, useFocusable, useStyles } from '../../hooks';
import type { Kind, VertSize } from './variants';
import {
  kind as getKind,
  vertSize as getVertSize,
  getAdditionalStyles,
} from './variants';
import { WHCM, WHCMstyles } from '../../constants';

export type TabItemOptions = {
  kind: Kind;
  vertSize: VertSize;
} & StylesOptions &
  TabOptions;

export type TabProps = TabItemOptions & ButtonHTMLProps;

const selectedTabSelector = [
  '&[aria-selected="true"]',
  '&[aria-selected="true"]:hover',
  '&[aria-selected="true"]:active',
];

const useTab = createHook<TabItemOptions, ButtonHTMLProps>({
  name: 'Tab',
  keys: ['kind', 'vertSize'],
  compose: [useTheme, useStyles, useFocusable, useReakitTab],
  useOptions(options) {
    return {
      ...options,
      focusable: true,
      styles: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...options.styles,
        ...getVertSize(options),
        ...getKind(options),
        ...getAdditionalStyles(options),

        [WHCM]: {
          [selectedTabSelector.join(',')]: {
            ...WHCMstyles,
            backgroundColor: 'WindowText',
            borderColor: 'WindowText',
            color: 'Window',
          },
        },
      } as SystemStyleObject,
    };
  },
});

export const Tab = createComponent({
  useHook: useTab,
  as: 'button',
  memo: true,
});
